/**
 * AI Form Schema Service
 * Handles AI-powered form schema generation
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { AI_FORM_SCHEMA_ENDPOINTS } from '../endpoints';

/**
 * @typedef {Object} GenerateFormSchemaRequestDto
 * @property {string} description - Form description (1-2000 chars, required)
 * @property {string} [industry] - Industry context (max 100 chars)
 * @property {string} [requirements] - Additional requirements (max 1000 chars)
 * @property {Object} [currentSchema] - Current FormSchemaDefinition to improve
 */

/**
 * @typedef {Object} ValidationMessage
 * @property {string} fieldKey - Field identifier
 * @property {string} message - Validation message
 * @property {'Error'|'Warning'|'Info'} severity - Message severity
 */

/**
 * @typedef {Object} SchemaValidationResultDto
 * @property {boolean} isValid - Whether schema is valid
 * @property {ValidationMessage[]} [errors] - Validation errors
 * @property {ValidationMessage[]} [warnings] - Validation warnings
 * @property {string[]} [suggestions] - Improvement suggestions
 */

/**
 * @typedef {Object} FormSchemaGenerationResultDto
 * @property {boolean} success - Whether generation succeeded
 * @property {Object} schemaDefinition - Generated FormSchemaDefinition
 * @property {string} [suggestedName] - AI-suggested form name
 * @property {string} [suggestedDescription] - AI-suggested form description
 * @property {string} [suggestedCategory] - AI-suggested category
 * @property {string} [suggestedTags] - AI-suggested tags
 * @property {SchemaValidationResultDto} validationResult - Schema validation results
 * @property {string} [errorMessage] - Error message if generation failed
 * @property {string} [reasoning] - AI reasoning for generated schema
 * @property {number[]} [referencedSchemaIds] - Referenced schema IDs
 * @property {number} [tokensUsed] - Number of AI tokens used
 * @property {string} generatedAt - Generation timestamp
 */

const aiFormSchemaService = {
  /**
   * Generate form schema using AI
   * @param {GenerateFormSchemaRequestDto} requestData - Generation request
   * @returns {Promise<FormSchemaGenerationResultDto>}
   */
  generateFormSchema: async (requestData) => {
    return apiRequest(() =>
      axiosInstance.post(AI_FORM_SCHEMA_ENDPOINTS.GENERATE, requestData, {
        timeout: 300000 // 5 minutes for AI generation
      })
    );
  },

  /**
   * Streaming version: calls the SSE endpoint, invoking callbacks as events arrive.
   * @param {GenerateFormSchemaRequestDto} requestData - Generation request
   * @param {{ onReasoning: (text: string) => void, onBuilding: () => void, onComplete: (result: Object) => void, onError: (msg: string) => void }} callbacks
   * @param {AbortSignal} [signal] - Optional abort signal
   * @returns {Promise<void>}
   */
  generateFormSchemaStream: async (requestData, callbacks, signal) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';
    const url = `${baseUrl}${AI_FORM_SCHEMA_ENDPOINTS.GENERATE_STREAM}`;

    const token = localStorage.getItem('localToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(requestData),
      signal,
    });

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('Rate limit reached. You can generate up to 30 forms per hour. Please wait a moment before trying again.');
      }
      if (response.status === 403) {
        throw new Error('Your plan does not allow AI generation. Please upgrade your license.');
      }
      const text = await response.text();
      throw new Error(text || `Request failed (HTTP ${response.status})`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by \n\n
      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';

      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith('data:')) continue;

        const jsonStr = line.slice(5).trim();
        try {
          const event = JSON.parse(jsonStr);
          if (event.type === 'reasoning' && callbacks.onReasoning) {
            callbacks.onReasoning(event.text);
          } else if (event.type === 'building' && callbacks.onBuilding) {
            callbacks.onBuilding();
          } else if (event.type === 'complete' && callbacks.onComplete) {
            callbacks.onComplete(event.result);
          } else if (event.type === 'error' && callbacks.onError) {
            callbacks.onError(event.message);
          }
        } catch {
          // ignore parse errors on individual events
        }
      }
    }
  },

  /**
   * Suggest next fields to add based on current schema (uses fast Haiku model)
   * @param {{ currentSchema: Object, formTitle?: string, industry?: string }} requestData
   * @returns {Promise<Array<{key: string, title: string, type: string, required: boolean, reason: string}>>}
   */
  suggestNextFields: async (requestData) => {
    return apiRequest(() =>
      axiosInstance.post(AI_FORM_SCHEMA_ENDPOINTS.SUGGEST_FIELDS, requestData, {
        timeout: 15000
      })
    );
  },

  /**
   * Refine an existing schema via natural language chat
   * @param {{ currentSchema: Object, message: string, conversationHistory: Array }} requestData
   * @returns {Promise<FormSchemaGenerationResultDto>}
   */
  refineSchema: async (requestData) => {
    return apiRequest(() =>
      axiosInstance.post(AI_FORM_SCHEMA_ENDPOINTS.REFINE, requestData, {
        timeout: 120000
      })
    );
  },

  /**
   * Validate request data before sending
   * @param {GenerateFormSchemaRequestDto} requestData - Request to validate
   * @returns {{isValid: boolean, errors: string[]}}
   */
  validateRequest: (requestData) => {
    const errors = [];

    if (!requestData.description || requestData.description.trim().length === 0) {
      errors.push('Description is required');
    }

    if (requestData.description && requestData.description.length > 2000) {
      errors.push('Description must not exceed 2000 characters');
    }

    if (requestData.industry && requestData.industry.length > 100) {
      errors.push('Industry must not exceed 100 characters');
    }

    if (requestData.requirements && requestData.requirements.length > 1000) {
      errors.push('Requirements must not exceed 1000 characters');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },
};

export default aiFormSchemaService;
